<template>
  <div class="twc-card">

    <!-- Header -->
    <div class="twc-header">
      <div class="twc-header-left">
        <i class="ti ti-sparkles" aria-hidden="true"></i>
        <span>Workforce cost forecast</span>
      </div>
      <span class="twc-sub">Next 60 days · Live data</span>
    </div>

    <!-- Top metrics -->
    <div class="twc-metrics">
      <div class="twc-metric">
        <div class="twc-metric-label">Baseline (last run)</div>
        <div class="twc-metric-val">RM {{ fmt(data.summary.total_baseline_cost) }}</div>
      </div>
      <div class="twc-metric twc-metric-arrow">→</div>
      <div class="twc-metric">
        <div class="twc-metric-label">Forecast (next run)</div>
        <div class="twc-metric-val" :style="forecastColor">
          RM {{ fmt(data.summary.total_forecast_cost) }}
        </div>
      </div>
      <div class="twc-metric">
        <div class="twc-metric-label">Variance</div>
        <div class="twc-metric-val" :style="varianceColor">
          {{ data.summary.total_variance >= 0 ? '+' : '' }}RM {{ fmt(Math.abs(data.summary.total_variance)) }}
        </div>
      </div>
    </div>

    <!-- Change drivers -->
    <div class="twc-drivers">
      <div class="twc-driver" v-if="data.summary.total_new_hires > 0">
        <div class="twc-driver-icon twc-icon-up">+</div>
        <div class="twc-driver-info">
          <div class="twc-driver-label">New hires ({{ data.summary.total_new_hires }} staff)</div>
          <div class="twc-driver-amt twc-up">+RM {{ fmt(data.summary.total_new_hire_cost) }}</div>
        </div>
      </div>
      <div class="twc-driver" v-if="data.summary.total_increments > 0">
        <div class="twc-driver-icon twc-icon-up">↑</div>
        <div class="twc-driver-info">
          <div class="twc-driver-label">Increments ({{ data.summary.total_increments }} staff)</div>
          <div class="twc-driver-amt twc-up">+RM {{ fmt(data.summary.total_increment_cost) }}</div>
        </div>
      </div>
      <div class="twc-driver" v-if="data.summary.total_exits > 0">
        <div class="twc-driver-icon twc-icon-down">-</div>
        <div class="twc-driver-info">
          <div class="twc-driver-label">Exits ({{ data.summary.total_exits }} staff)</div>
          <div class="twc-driver-amt twc-down">-RM {{ fmt(data.summary.total_exit_saving) }}</div>
        </div>
      </div>
      <div class="twc-driver" v-if="data.summary.total_new_hires === 0 && data.summary.total_increments === 0 && data.summary.total_exits === 0">
        <div class="twc-driver-icon twc-icon-stable">✓</div>
        <div class="twc-driver-info">
          <div class="twc-driver-label">No changes detected in next 60 days</div>
          <div class="twc-driver-amt" style="color:#27500A">Cost expected to remain stable</div>
        </div>
      </div>
    </div>

    <!-- Per company breakdown -->
    <template v-if="data.companies && data.companies.length > 0">
      <div class="twc-section-title">By company</div>
      <div class="twc-company-list">
        <div v-for="(co, i) in data.companies" :key="i" class="twc-company-row">
          <div class="twc-company-info">
            <div class="twc-company-name">{{ co.baseline_payoutdesc }}</div>
            <div class="twc-company-sub">
              {{ co.baseline_staff_count }} → {{ co.forecast_staff_count }} staff ·
              Statutory ratio: {{ co.stat_ratio_used }}%
            </div>
          </div>
          <div class="twc-company-costs">
            <div class="twc-cost-row">
              <span class="twc-cost-label">Baseline</span>
              <span class="twc-cost-val">RM {{ fmt(co.baseline_employer_cost) }}</span>
            </div>
            <div class="twc-cost-row">
              <span class="twc-cost-label">Forecast</span>
              <span class="twc-cost-val" :style="{ color: co.forecast_variance >= 0 ? '#A32D2D' : '#27500A' }">
                RM {{ fmt(co.forecast_cost) }}
              </span>
            </div>
          </div>
          <span class="twc-badge" :class="alertClass(co.alert)">{{ co.alert }}</span>
        </div>
      </div>

      <!-- Statutory breakdown for first company -->
      <div v-if="data.companies[0]" class="twc-stat-breakdown">
        <div class="twc-section-title">Employer statutory (baseline)</div>
        <div class="twc-stat-row">
          <span>EPF (Employer)</span>
          <span>RM {{ fmt(data.companies[0].stat_breakdown.coepf) }}</span>
        </div>
        <div class="twc-stat-row">
          <span>SOCSO</span>
          <span>RM {{ fmt(data.companies[0].stat_breakdown.cosocso) }}</span>
        </div>
        <div class="twc-stat-row">
          <span>EIS</span>
          <span>RM {{ fmt(data.companies[0].stat_breakdown.coeis) }}</span>
        </div>
        <div class="twc-stat-row">
          <span>HRDF</span>
          <span>RM {{ fmt(data.companies[0].stat_breakdown.hrdf) }}</span>
        </div>
      </div>
    </template>

    <!-- Footer -->
    <div class="twc-footer">
      <button class="twc-btn" @click="askMore('Show me which staff are joining or leaving that affect next month cost')">
        Who is changing ↗
      </button>
      <button class="twc-btn" @click="askMoreWithContext()">
        Cost analysis ↗
      </button>
    </div>

  </div>
</template>

<script>
export default {
  name: "TaraWorkforceCostCard",
  props: { data: { type: Object, required: true } },
  computed: {
    totalVariancePct() {
      const base = this.data.summary.total_baseline_cost;
      if (!base) return 0;
      return ((this.data.summary.total_variance / base) * 100).toFixed(1);
    },
    forecastColor() {
      return { color: this.data.summary.total_variance >= 0 ? '#A32D2D' : '#27500A', fontWeight: '500' };
    },
    varianceColor() {
      return { color: this.data.summary.total_variance >= 0 ? '#A32D2D' : '#27500A' };
    },
  },
  methods: {
    fmt(val) {
      return Number(val || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    alertClass(alert) {
      if (alert === 'HIGH')   return 'twc-badge-high';
      if (alert === 'MEDIUM') return 'twc-badge-med';
      return 'twc-badge-stable';
    },
    askMore(text) { this.$emit('ask', text); },
    askMoreWithContext() {
      const baseline = this.fmt(this.data.summary.total_baseline_cost);
      const forecast = this.fmt(this.data.summary.total_forecast_cost);
      const variance = this.fmt(Math.abs(this.data.summary.total_variance));
      const direction = this.data.summary.total_variance >= 0 ? 'increase' : 'decrease';
      const drivers = [];
      if (this.data.summary.total_new_hires > 0)
        drivers.push(`${this.data.summary.total_new_hires} new hire(s) costing RM ${this.fmt(this.data.summary.total_new_hire_cost)}`);
      if (this.data.summary.total_increments > 0)
        drivers.push(`${this.data.summary.total_increments} increment(s) adding RM ${this.fmt(this.data.summary.total_increment_cost)}`);
      if (this.data.summary.total_exits > 0)
        drivers.push(`${this.data.summary.total_exits} exit(s) saving RM ${this.fmt(this.data.summary.total_exit_saving)}`);
      const driverText = drivers.length > 0 ? drivers.join(', ') : 'no staff changes detected';
      this.$emit('ask', `My payroll baseline is RM ${baseline}, forecast is RM ${forecast} (${direction} of RM ${variance}). Drivers: ${driverText}. Please analyse this and suggest how to manage the cost.`);
    },
  },
};
</script>

<style scoped>
.twc-card { border:0.5px solid #AFA9EC; border-radius:12px; background:#FAFAFE; overflow:hidden; font-family:inherit; font-size:13px; margin:4px 0 8px; max-width:460px; }
.twc-header { display:flex; align-items:center; justify-content:space-between; padding:10px 14px 8px; background:#EEEDFE; border-bottom:0.5px solid #AFA9EC; }
.twc-header-left { display:flex; align-items:center; gap:6px; font-weight:500; color:#3C3489; font-size:13px; }
.twc-header-left i { font-size:15px; }
.twc-sub { font-size:11px; color:#534AB7; }
.twc-metrics { display:flex; align-items:center; gap:0; padding:12px 14px; border-bottom:0.5px solid #E5E3F8; background:#F5F4FD; }
.twc-metric { flex:1; text-align:center; }
.twc-metric-arrow { flex:0; font-size:18px; color:#AFA9EC; padding:0 6px; }
.twc-metric-label { font-size:10px; color:#7F77DD; margin-bottom:3px; }
.twc-metric-val { font-size:15px; font-weight:500; color:#26215C; }
.twc-drivers { padding:10px 14px; border-bottom:0.5px solid #E5E3F8; }
.twc-driver { display:flex; align-items:center; gap:10px; padding:5px 0; }
.twc-driver-icon { width:24px; height:24px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:13px; font-weight:700; flex-shrink:0; }
.twc-icon-up   { background:#FCEBEB; color:#791F1F; }
.twc-icon-down { background:#EAF3DE; color:#27500A; }
.twc-icon-stable { background:#E6F1FB; color:#0C447C; }
.twc-driver-info { flex:1; }
.twc-driver-label { font-size:12px; color:#26215C; }
.twc-driver-amt { font-size:12px; font-weight:500; margin-top:1px; }
.twc-up   { color:#A32D2D; }
.twc-down { color:#27500A; }
.twc-section-title { font-size:11px; font-weight:500; color:#7F77DD; padding:8px 14px 4px; text-transform:uppercase; letter-spacing:0.05em; }
.twc-company-list { padding:0 0 4px; }
.twc-company-row { display:flex; align-items:center; gap:8px; padding:8px 14px; border-bottom:0.5px solid #F0EFF9; }
.twc-company-row:last-child { border-bottom:none; }
.twc-company-info { flex:1; min-width:0; }
.twc-company-name { font-size:12px; font-weight:500; color:#26215C; }
.twc-company-sub { font-size:10px; color:#7F77DD; margin-top:1px; }
.twc-company-costs { min-width:140px; }
.twc-cost-row { display:flex; justify-content:space-between; gap:8px; }
.twc-cost-label { font-size:10px; color:#7F77DD; }
.twc-cost-val { font-size:11px; font-weight:500; color:#26215C; }
.twc-badge { font-size:10px; font-weight:500; padding:2px 6px; border-radius:20px; flex-shrink:0; white-space:nowrap; }
.twc-badge-high   { background:#FCEBEB; color:#791F1F; }
.twc-badge-med    { background:#FAEEDA; color:#633806; }
.twc-badge-stable { background:#EAF3DE; color:#27500A; }
.twc-stat-breakdown { padding:4px 14px 10px; border-top:0.5px solid #E5E3F8; }
.twc-stat-row { display:flex; justify-content:space-between; padding:3px 0; font-size:11px; color:#7F77DD; border-bottom:0.5px solid #F5F4FD; }
.twc-stat-row:last-child { border-bottom:none; }
.twc-footer { display:flex; gap:8px; padding:10px 14px; background:#F5F4FD; border-top:0.5px solid #E5E3F8; }
.twc-btn { font-size:12px; padding:5px 10px; border-radius:6px; border:0.5px solid #AFA9EC; background:white; color:#3C3489; cursor:pointer; }
.twc-btn:hover { background:#EEEDFE; }
</style>